/**
 * T12DataCard Component
 *
 * Displays T12 operating history when available:
 * - T12 NOI: Actual Net Operating Income from trailing 12 months
 * - T12 Cap Rate: T12 NOI / Purchase Price (what you're buying)
 * - NOI Uplift: Shows gap between T12 and stabilized NOI
 *
 * Uses amber theme to distinguish actual data from pro-forma projections.
 * Only renders when T12 data is provided and > 0.
 *
 * Design: Refined Financial Terminal aesthetic - Bloomberg meets modern fintech.
 */

import { TrendingUp, AlertCircle, Activity } from 'lucide-react';
import { formatCurrency, formatPercent } from '../../utils/formatters';

export interface T12DataCardProps {
  t12Noi: number;
  purchasePrice: number;
  stabilizedNoi: number;
}

// Refined color palette - warm gold/amber financial theme
const colors = {
  // Card background with subtle noise texture effect via gradient
  cardBg: 'linear-gradient(135deg, #1c1917 0%, #292524 50%, #1c1917 100%)',
  cardBorder: '#b45309',
  // Gold accent spectrum
  goldBright: '#fbbf24',
  goldMuted: '#d97706',
  goldDark: '#92400e',
  // Metric backgrounds
  metricBg: 'linear-gradient(180deg, rgba(251,191,36,0.08) 0%, rgba(217,119,6,0.04) 100%)',
  metricBorder: 'rgba(251,191,36,0.2)',
  // Text
  textPrimary: '#fef3c7',
  textSecondary: '#fcd34d',
  textMuted: '#a8a29e',
  // Accent colors for uplift
  upliftPositive: '#34d399',
  upliftPositiveBg: 'rgba(52,211,153,0.1)',
  upliftNegative: '#f87171',
  upliftNegativeBg: 'rgba(248,113,113,0.1)',
  // Stabilized state
  stabilizedBlue: '#60a5fa',
  stabilizedBg: 'rgba(96,165,250,0.1)',
};

export function T12DataCard({ t12Noi, purchasePrice, stabilizedNoi }: T12DataCardProps) {
  if (!t12Noi || t12Noi <= 0 || !purchasePrice || purchasePrice <= 0) {
    return null;
  }

  const t12CapRate = t12Noi / purchasePrice;
  const noiUplift = stabilizedNoi - t12Noi;
  const noiUpliftPct = t12Noi > 0 ? (noiUplift / t12Noi) * 100 : 0;
  const hasUpside = t12Noi < stabilizedNoi;

  // Styles
  const cardStyle: React.CSSProperties = {
    background: colors.cardBg,
    border: `1px solid ${colors.cardBorder}`,
    borderRadius: '12px',
    padding: '24px',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0 4px 24px rgba(180,83,9,0.15), inset 0 1px 0 rgba(251,191,36,0.1)',
  };

  // Decorative corner accent
  const cornerAccentStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    right: 0,
    width: '120px',
    height: '120px',
    background: `radial-gradient(circle at top right, rgba(251,191,36,0.15) 0%, transparent 70%)`,
    pointerEvents: 'none',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: '20px',
  };

  const titleStyle: React.CSSProperties = {
    fontFamily: '"JetBrains Mono", "SF Mono", "Fira Code", monospace',
    fontSize: '13px',
    fontWeight: 600,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    color: colors.goldBright,
    marginBottom: '4px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  };

  const subtitleStyle: React.CSSProperties = {
    fontFamily: '"Inter", -apple-system, sans-serif',
    fontSize: '12px',
    color: colors.textMuted,
    fontWeight: 400,
  };

  const badgeStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    background: 'linear-gradient(135deg, rgba(251,191,36,0.2) 0%, rgba(217,119,6,0.15) 100%)',
    border: `1px solid ${colors.goldMuted}`,
    borderRadius: '6px',
    fontFamily: '"JetBrains Mono", monospace',
    fontSize: '10px',
    fontWeight: 600,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: colors.goldBright,
  };

  const metricsGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px',
    marginBottom: '16px',
  };

  const metricCardStyle: React.CSSProperties = {
    background: colors.metricBg,
    border: `1px solid ${colors.metricBorder}`,
    borderRadius: '8px',
    padding: '16px',
    position: 'relative',
  };

  const metricLabelStyle: React.CSSProperties = {
    fontFamily: '"JetBrains Mono", monospace',
    fontSize: '10px',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: colors.goldMuted,
    marginBottom: '8px',
  };

  const metricValueStyle: React.CSSProperties = {
    fontFamily: '"JetBrains Mono", monospace',
    fontSize: '22px',
    fontWeight: 700,
    color: colors.textPrimary,
    letterSpacing: '-0.02em',
    lineHeight: 1.1,
  };

  const metricHintStyle: React.CSSProperties = {
    fontFamily: '"Inter", sans-serif',
    fontSize: '11px',
    color: colors.textMuted,
    marginTop: '6px',
  };

  const upliftValueStyle: React.CSSProperties = {
    ...metricValueStyle,
    color: noiUplift >= 0 ? colors.upliftPositive : colors.upliftNegative,
  };

  const calloutStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '14px 16px',
    borderRadius: '8px',
    background: hasUpside ? colors.upliftPositiveBg : colors.stabilizedBg,
    border: `1px solid ${hasUpside ? 'rgba(52,211,153,0.25)' : 'rgba(96,165,250,0.25)'}`,
  };

  const calloutIconStyle: React.CSSProperties = {
    flexShrink: 0,
    marginTop: '2px',
    color: hasUpside ? colors.upliftPositive : colors.stabilizedBlue,
  };

  const calloutTitleStyle: React.CSSProperties = {
    fontFamily: '"Inter", sans-serif',
    fontSize: '13px',
    fontWeight: 600,
    color: hasUpside ? colors.upliftPositive : colors.stabilizedBlue,
    marginBottom: '4px',
  };

  const calloutTextStyle: React.CSSProperties = {
    fontFamily: '"Inter", sans-serif',
    fontSize: '12px',
    color: colors.textMuted,
    lineHeight: 1.4,
  };

  const footerStyle: React.CSSProperties = {
    marginTop: '16px',
    paddingTop: '12px',
    borderTop: `1px solid rgba(251,191,36,0.1)`,
    fontFamily: '"Inter", sans-serif',
    fontSize: '11px',
    color: colors.textMuted,
    fontStyle: 'italic',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  };

  const pulseKeyframes = `
    @keyframes pulse-gold {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.6; }
    }
  `;

  return (
    <>
      <style>{pulseKeyframes}</style>
      <div style={cardStyle}>
        <div style={cornerAccentStyle} />

        {/* Header */}
        <div style={headerStyle}>
          <div>
            <div style={titleStyle}>
              <Activity size={14} style={{ color: colors.goldBright }} />
              T12 Operating History
            </div>
            <div style={subtitleStyle}>Trailing 12-month actual performance</div>
          </div>
          <div style={badgeStyle}>
            <span style={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              background: colors.goldBright,
              animation: 'pulse-gold 2s ease-in-out infinite',
            }} />
            Verified Actuals
          </div>
        </div>

        {/* Metrics Grid */}
        <div style={metricsGridStyle}>
          {/* T12 NOI */}
          <div style={metricCardStyle}>
            <div style={metricLabelStyle}>T12 NOI</div>
            <div style={metricValueStyle}>{formatCurrency(t12Noi)}</div>
            <div style={metricHintStyle}>Actual annual income</div>
          </div>

          {/* T12 Cap Rate */}
          <div style={metricCardStyle}>
            <div style={metricLabelStyle}>T12 Cap Rate</div>
            <div style={metricValueStyle}>{formatPercent(t12CapRate * 100, 2)}</div>
            <div style={metricHintStyle}>At purchase price</div>
          </div>

          {/* NOI Uplift */}
          <div style={metricCardStyle}>
            <div style={metricLabelStyle}>NOI Uplift</div>
            <div style={upliftValueStyle}>
              {noiUplift >= 0 ? '+' : ''}{formatCurrency(noiUplift)}
            </div>
            <div style={metricHintStyle}>
              vs Pro-Forma ({noiUpliftPct >= 0 ? '+' : ''}{noiUpliftPct.toFixed(1)}%)
            </div>
          </div>
        </div>

        {/* Upside Analysis Callout */}
        <div style={calloutStyle}>
          {hasUpside ? (
            <TrendingUp size={18} style={calloutIconStyle} />
          ) : (
            <AlertCircle size={18} style={calloutIconStyle} />
          )}
          <div>
            <div style={calloutTitleStyle}>
              {hasUpside
                ? `Upside Potential: ${formatCurrency(noiUplift)} NOI growth opportunity`
                : 'Already Stabilized - Pro-forma may be conservative'
              }
            </div>
            <div style={calloutTextStyle}>
              {hasUpside
                ? `Stabilized NOI (${formatCurrency(stabilizedNoi)}) exceeds T12 by ${noiUpliftPct.toFixed(0)}%`
                : 'T12 NOI already at or above stabilized projections'
              }
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={footerStyle}>
          <span style={{ color: colors.goldMuted }}>*</span>
          T12 data represents actual operating history from property financials
        </div>
      </div>
    </>
  );
}

export default T12DataCard;
